'use client';

import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

interface LayoutModeContextType {
  /** When true, layout-body removes max-width constraint */
  fullWidth: boolean;
  setFullWidth: (value: boolean) => void;
}

const LayoutModeContext = createContext<LayoutModeContextType | undefined>(undefined);

export function LayoutModeProvider({ children }: { children: ReactNode }) {
  const [fullWidth, setFullWidth] = useState(false);

  return (
    <LayoutModeContext.Provider value={{ fullWidth, setFullWidth }}>
      {children}
    </LayoutModeContext.Provider>
  );
}

export function useLayoutMode() {
  const context = useContext(LayoutModeContext);
  if (context === undefined) {
    throw new Error('useLayoutMode must be used within a LayoutModeProvider');
  }
  return context;
}

/** Optional hook that returns null if outside provider */
export function useLayoutModeOptional() {
  return useContext(LayoutModeContext);
}

/**
 * Hook for pages to declare they want full-width layout.
 * Sets fullWidth=true on mount, resets on unmount.
 */
export function useFullWidthLayout() {
  const context = useLayoutModeOptional();

  useEffect(() => {
    if (!context) return;
    context.setFullWidth(true);
    return () => context.setFullWidth(false);
  }, [context]);
}
